import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  acceptanceTerms,
  brokerQuotes,
  operations,
  quoteFinancial,
  type BrokerQuoteOptions,
  type OperationDetail,
  type TermsCurrentVersion,
} from '@/shared/api/resources';
import {
  formatCivilDate,
  formatDocument,
  formatMoney,
  formatRate,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { RichTextView } from '@/shared/ui/RichTextEditor';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { ContractualTermsEditor } from '../ContractualTermsEditor';
import {
  chaveDoFinanceiro,
  formaDePagamento,
  useFinanceiroDaCotacao,
} from '../financeiroDaCotacao';
import { ParticularClausesSection } from '../ParticularClausesDialog';
import type { Sugestao } from '../sugestoesDaOperacao';
import {
  AVISO_TAGS_NAO_PREENCHIDAS,
  temTagVaziaNoBlocoHabilitado,
  useTermosDaCotacao,
} from '../termosDaCotacao';
import { fieldErrorOf } from '../../shared/fieldError';

import { avisoDoNumeroDeParcelas } from './cotacaoDaCorretora';
import { enderecoDoTomador } from './enderecoDoTomador';
import { QuoteWithdrawDialog } from './QuoteWithdrawDialog';

const caixa =
  'mt-0.5 size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25';

/**
 * Passo "Objeto da apolice" (NCT-131 a NCT-140): o editor "Objetos" com as TAGs e o
 * texto rico, e as clausulas particulares que o cadastro deixa a corretora inserir. Sao
 * os mesmos editores da seguradora (P-COT-2), com a edicao da propria cotacao.
 */
export function ObjetoStep({ operacao }: { operacao: OperationDetail }) {
  const termos = useTermosDaCotacao(operacao.id);
  return (
    <div className="flex flex-col gap-5">
      {temTagVaziaNoBlocoHabilitado(termos.data) ? (
        // NCT-133: so aviso; a cotacao segue e pode ser enviada.
        <Alert tone="warning" title={AVISO_TAGS_NAO_PREENCHIDAS}>
          Um bloco habilitado do objeto tem TAG sem valor. O aviso nao impede seguir nem
          enviar a cotacao.
        </Alert>
      ) : null}
      <ContractualTermsEditor operacao={operacao} />
      <ParticularClausesSection operacao={operacao} />
    </div>
  );
}

/**
 * Passo "Dados financeiros" (NCT-149 a NCT-162): a comissao, gravada, e a forma de
 * pagamento e o numero de parcelas, gravados no financeiro da cotacao (P-COT-4, ligado
 * pelo P-NCT-L). As classes de parcelamento que a empresa oferece ficam so mostradas.
 *
 * Nada e calculado: o valor das parcelas, o adicional de fracionamento, o vencimento
 * ("Dias") e qual classe vale para o tomador sao regra pendente (PAR-001 a PAR-007,
 * PAR-247) e aparecem como pendentes. As parcelas com valor sao informadas pela
 * seguradora, por alcada.
 */
export function PagamentoStep({
  operacao,
  opcoes,
  sugestaoDaComissao,
}: {
  operacao: OperationDetail;
  opcoes: BrokerQuoteOptions;
  /**
   * A sugestao da comissao, a mesma da abertura (OPEN_QUESTIONS 2.97). A origem aparece
   * enquanto o campo tem o valor sugerido; o campo continua editavel.
   */
  sugestaoDaComissao?: Sugestao | undefined;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [comissao, setComissao] = useState(rateToInput(operacao.commission_bps));
  const podeEditar = operacao.quote_actions?.can_edit === true;
  const origemDaComissao =
    sugestaoDaComissao &&
    sugestaoDaComissao.valor !== '' &&
    comissao.trim() !== '' &&
    rateInputError(comissao) === undefined &&
    rateToBasisPoints(comissao) === rateToBasisPoints(sugestaoDaComissao.valor)
      ? sugestaoDaComissao.origem
      : undefined;

  const gravar = useMutation({
    mutationFn: () =>
      operations.update(operacao.id, { commission_bps: rateToBasisPoints(comissao) }),
    onSuccess: () => {
      toast.success('Comissao gravada.');
      void queryClient.invalidateQueries({ queryKey: ['operations', operacao.id] });
    },
  });
  const apiError = gravar.error instanceof ApiError ? gravar.error : null;

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader title="Dados financeiros" description="Comissao da corretora." />
        <CardBody className="flex flex-col gap-3">
          {gravar.isError && !fieldErrorOf(apiError, 'commission_bps') ? (
            <Alert tone="danger" title="Nao foi possivel gravar a comissao">
              {gravar.error.message}
            </Alert>
          ) : null}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-56">
              <Field
                label="Comissao (%)"
                inputMode="decimal"
                value={comissao}
                disabled={!podeEditar}
                error={
                  rateInputError(comissao) ?? fieldErrorOf(apiError, 'commission_bps')
                }
                hint={origemDaComissao}
                onChange={(event) => setComissao(event.target.value)}
              />
            </div>
            {podeEditar ? (
              <Button
                variant="secondary"
                loading={gravar.isPending}
                disabled={rateInputError(comissao) !== undefined}
                onClick={() => gravar.mutate()}
              >
                Gravar comissao
              </Button>
            ) : null}
          </div>
          <p className="text-2xs text-ink-500">
            A comissao maxima da modalidade ainda nao e conferida: a regra e pendente
            (PRC-005).
          </p>
        </CardBody>
      </Card>

      <FormaDePagamentoCard operacao={operacao} opcoes={opcoes} />

      <Card>
        <CardHeader
          title="Parcelamento da empresa"
          description="As classes de parcelamento ativas, so para consulta."
        />
        <CardBody className="flex flex-col gap-4">
          <div>
            <p className="text-sm font-medium text-ink-800">Classes de parcelamento</p>
            {opcoes.installment_classes.length === 0 ? (
              <p className="text-sm text-ink-500">
                Nenhuma classe de parcelamento ativa.
              </p>
            ) : (
              <ul className="mt-1 flex flex-col divide-y divide-line">
                {opcoes.installment_classes.map((classe) => (
                  <li key={classe.id} className="py-2 text-xs text-ink-700">
                    <p className="font-medium text-ink-900">{classe.name}</p>
                    <p>
                      {classe.max_installments
                        ? `Ate ${classe.max_installments} parcela(s)`
                        : 'Sem numero maximo de parcelas'}
                      {classe.min_installment
                        ? `, parcela minima de ${formatMoney(classe.min_installment)}`
                        : ''}
                      {classe.monthly_interest_bps
                        ? `, juros de ${formatRate(classe.monthly_interest_bps)} ao mes a partir da parcela ${classe.interest_from_installment ?? '-'}`
                        : ''}
                      .
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * "Forma de pagamento" (NCT-152) e "Numero de parcelas" (NCT-153), gravados no
 * financeiro da cotacao pela edicao propria da corretora (antes do envio). Ao lado, o que
 * o OnPoint calcula e aqui segue pendente de regra, sem valor inventado.
 */
function FormaDePagamentoCard({
  operacao,
  opcoes,
}: {
  operacao: OperationDetail;
  opcoes: BrokerQuoteOptions;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const financeiro = useFinanceiroDaCotacao(operacao.id);
  const podeEditar = operacao.quote_actions?.can_edit === true;
  // null: o campo ainda mostra o que esta gravado.
  const [meio, setMeio] = useState<string | null>(null);
  const [parcelas, setParcelas] = useState<string | null>(null);

  const dados = financeiro.data;
  const meioNaTela = meio ?? dados?.payment_method_id ?? '';
  const parcelasNaTela =
    parcelas ??
    (dados?.installments_count === null || dados?.installments_count === undefined
      ? ''
      : String(dados.installments_count));
  const avisoDasParcelas = avisoDoNumeroDeParcelas(parcelasNaTela);

  const gravar = useMutation({
    mutationFn: () =>
      quoteFinancial.update(operacao.id, {
        payment_method_id: meioNaTela === '' ? null : meioNaTela,
        installments_count: parcelasNaTela === '' ? null : Number(parcelasNaTela),
      }),
    onSuccess: (novo) => {
      toast.success('Forma de pagamento e parcelas gravadas.');
      queryClient.setQueryData(chaveDoFinanceiro(operacao.id), novo);
      setMeio(null);
      setParcelas(null);
      void queryClient.invalidateQueries({ queryKey: ['quote-timeline', operacao.id] });
    },
  });
  const apiError = gravar.error instanceof ApiError ? gravar.error : null;
  const erroDoCampo =
    fieldErrorOf(apiError, 'payment_method_id') ??
    fieldErrorOf(apiError, 'installments_count');

  // A forma gravada que saiu da lista de ativas continua na escolha, com o aviso.
  const meios = opcoes.payment_methods.map((item) => ({
    value: item.id,
    label: item.label,
  }));
  if (
    dados?.payment_method_id &&
    !meios.some((item) => item.value === dados.payment_method_id)
  ) {
    meios.push({
      value: dados.payment_method_id,
      label: `${dados.payment_method_label} (inativa)`,
    });
  }
  const primeira = dados?.installments.first_installment ?? null;
  const itens = dados?.installments.items ?? [];

  return (
    <Card>
      <CardHeader
        title="Forma de pagamento e parcelamento"
        description="A forma de pagamento e o numero de parcelas da cotacao."
      />
      <CardBody className="flex flex-col gap-4">
        {financeiro.isError ? (
          <Alert
            tone="danger"
            title="Nao foi possivel carregar o financeiro da cotacao"
          >
            {financeiro.error.message}
          </Alert>
        ) : null}
        {gravar.isError && !erroDoCampo ? (
          <Alert tone="danger" title="Nao foi possivel gravar">
            {gravar.error.message}
          </Alert>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Forma de pagamento"
            placeholder="Selecione o meio de pagamento"
            value={meioNaTela}
            options={meios}
            disabled={!podeEditar || !dados}
            hint={
              opcoes.payment_methods.length === 0
                ? 'A empresa ainda nao tem forma de pagamento ativa.'
                : undefined
            }
            error={fieldErrorOf(apiError, 'payment_method_id')}
            onChange={(event) => setMeio(event.target.value)}
          />
          <Field
            label="Numero de parcelas"
            inputMode="numeric"
            value={parcelasNaTela}
            disabled={!podeEditar || !dados}
            error={avisoDasParcelas ?? fieldErrorOf(apiError, 'installments_count')}
            onChange={(event) => setParcelas(event.target.value)}
          />
        </div>
        {dados && !dados.payment_method_id && dados.payment_method_label ? (
          <p className="text-xs text-ink-500">
            Forma gravada: {formaDePagamento(dados)}.
          </p>
        ) : null}
        {podeEditar ? (
          <div className="flex justify-end">
            <Button
              variant="secondary"
              loading={gravar.isPending}
              disabled={!dados || avisoDasParcelas !== undefined}
              onClick={() => gravar.mutate()}
            >
              Gravar forma de pagamento
            </Button>
          </div>
        ) : null}

        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <Item rotulo="1a parcela">
            {primeira
              ? `${formatMoney(primeira.amount)} em ${formatCivilDate(primeira.due_on)}`
              : 'Pendente de regra (PAR-002): informada pela seguradora'}
          </Item>
          <Item rotulo="Adicional de fracionamento na parcela">
            Pendente de regra (PRC-007, PAR-004, PAR-005)
          </Item>
          <Item rotulo="Dias (vencimento da 1a parcela)">
            Pendente de regra (PAR-003; o significado do campo esta a confirmar)
          </Item>
          <Item rotulo="Classe de parcelamento do tomador">A confirmar (PAR-247)</Item>
        </dl>

        {itens.length > 0 ? (
          <div>
            <p className="text-sm font-medium text-ink-800">Parcelas</p>
            <ul className="mt-1 flex flex-col divide-y divide-line">
              {itens.map((item) => (
                <li key={item.id} className="py-1.5 text-xs text-ink-700">
                  {item.number}a de {itens.length}: {formatMoney(item.amount)} em{' '}
                  {formatCivilDate(item.due_on)}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-xs text-ink-500">
            Nenhuma parcela com valor ainda. O valor e o vencimento das parcelas sao
            informados pela seguradora: o calculo e regra pendente (PAR-001 a PAR-007),
            e o numero escolhido aqui nao divide o premio.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Passo "Resumo da cotacao" (NCT-161 a NCT-180): o que foi gravado, a confirmacao de
 * revisao, "Enviar para analise" (a cotacao vai para a analise manual da seguradora),
 * "Seguir para emissao" desligado e "Voltar para cotacoes".
 *
 * No OnPoint, "Seguir para emissao" aparece quando as verificacoes automaticas aprovam
 * a cotacao; elas sao regra pendente (D-05), e o pedido de emissao fica na pagina da
 * cotacao depois da aprovacao da seguradora.
 */
export function ResumoStep({
  operacao,
  opcoes,
  termo,
  declarou,
}: {
  operacao: OperationDetail;
  opcoes: BrokerQuoteOptions;
  termo: TermsCurrentVersion | null | undefined;
  declarou: boolean;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [revisou, setRevisou] = useState(false);
  const [declaracao, setDeclaracao] = useState(declarou);
  const [cancelando, setCancelando] = useState(false);
  const precisaDeclarar = termo !== null && termo !== undefined;

  const enviar = useMutation({
    mutationFn: async () => {
      if (termo) await acceptanceTerms.approve(termo.id, { operation_id: operacao.id });
      return brokerQuotes.submit(operacao.id);
    },
    onSuccess: () => {
      toast.success('Encaminhado para analise manual.');
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void navigate(`/operacoes/${operacao.id}`);
    },
  });

  const podeEnviar = operacao.quote_actions?.can_submit === true;
  const bloqueado = !revisou || (precisaDeclarar && !declaracao);

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader
          title="Resumo da cotacao"
          description={`Cotacao ${operacao.proposal_number ?? operacao.number}`}
        />
        <CardBody>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <Item rotulo="Tomador">
              {operacao.policyholder.label}
              {operacao.policyholder.document
                ? ` - ${formatDocument(operacao.policyholder.document)}`
                : ''}
            </Item>
            <Item rotulo="Filial">
              {operacao.policyholder_branch?.label ?? 'Matriz'}
            </Item>
            {operacao.policyholder.address !== undefined ? (
              <Item rotulo="Endereco do tomador">
                {enderecoDoTomador(operacao.policyholder.address)}
              </Item>
            ) : null}
            <Item rotulo="Segurado">
              {operacao.insured
                ? `${operacao.insured.label} - ${formatDocument(operacao.insured.document)}`
                : '-'}
            </Item>
            <Item rotulo="Endereco do segurado">
              {operacao.insured_address?.label ?? '-'}
            </Item>
            <Item rotulo="Modalidade">{operacao.modality.label}</Item>
            <Item rotulo="Modalidade complementar">
              {operacao.complementary_modality?.label ?? '-'}
            </Item>
            <Item rotulo="Importancia Segurada">
              {formatMoney(operacao.insured_amount)}
            </Item>
            <Item rotulo="Taxa">{formatRate(operacao.applied_rate_bps)}</Item>
            <Item rotulo="Premio">{formatMoney(operacao.premium)}</Item>
            <Item rotulo="Comissao">{formatRate(operacao.commission_bps)}</Item>
            <Item rotulo="Vigencia">
              {formatCivilDate(operacao.coverage_start)} a{' '}
              {formatCivilDate(operacao.coverage_end)}
              {operacao.term_days ? ` (${operacao.term_days} dias)` : ''}
            </Item>
            <Item rotulo="Canal">
              {operacao.channel?.label ?? 'Canal nao definido'}
            </Item>
          </dl>
          <p className="mt-3 text-sm text-ink-700">{operacao.subject}</p>
        </CardBody>
      </Card>

      <CondicoesParticularesDoResumo operationId={operacao.id} />

      {opcoes.pending_rules.length > 0 ? (
        <Alert tone="info" title="Regras ainda pendentes">
          O premio e o IS x taxa da modalidade principal. Ficam para a seguradora:{' '}
          {opcoes.pending_rules.map((regra) => regra.code).join(', ')}.
        </Alert>
      ) : null}

      <Card>
        <CardBody className="flex flex-col gap-3">
          {enviar.isError ? (
            <Alert tone="danger" title="Nao foi possivel enviar para analise">
              {enviar.error.message}
            </Alert>
          ) : null}
          {precisaDeclarar && !declarou ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-ink-800">{termo.title}</p>
              <div className="max-h-48 overflow-y-auto rounded-md border border-line p-3">
                <RichTextView html={termo.body} />
              </div>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={declaracao}
                  onChange={(event) => setDeclaracao(event.target.checked)}
                  className={caixa}
                />
                Declaro que li e estou ciente das condicoes acima.
              </label>
            </div>
          ) : null}
          <label className="flex cursor-pointer items-start gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={revisou}
              onChange={(event) => setRevisou(event.target.checked)}
              className={caixa}
            />
            Por favor, confirme que revisou todas as informacoes.
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            <Link
              to="/operacoes"
              className="inline-flex h-10 items-center rounded-md px-3 text-sm font-medium text-ink-600 transition-colors duration-(--duration-fast) hover:bg-ink-100"
            >
              Voltar para cotacoes
            </Link>
            {operacao.quote_actions?.can_withdraw ? (
              <Button variant="danger" onClick={() => setCancelando(true)}>
                Cancelar cotacao
              </Button>
            ) : null}
            <Button
              variant="secondary"
              disabled
              title="A emissao e pedida depois da aprovacao da seguradora: as verificacoes automaticas que a liberariam aqui sao regra pendente (D-05)."
            >
              Seguir para emissao
            </Button>
            {podeEnviar ? (
              <Button
                loading={enviar.isPending}
                disabled={bloqueado}
                onClick={() => enviar.mutate()}
              >
                Enviar para analise
              </Button>
            ) : null}
          </div>
        </CardBody>
      </Card>

      <QuoteWithdrawDialog
        operationId={operacao.id}
        open={cancelando}
        onClose={() => setCancelando(false)}
        onDone={() => void navigate('/operacoes')}
      />
    </div>
  );
}

/**
 * "Condicoes particulares" do Resumo da cotacao do OnPoint (NCT-172): as clausulas
 * escolhidas na cotacao, pelo nome, com o que ficou fora da minuta.
 */
function CondicoesParticularesDoResumo({ operationId }: { operationId: string }) {
  const termos = useTermosDaCotacao(operationId);
  const clausulas = (termos.data?.items ?? []).filter(
    (termo) => termo.kind === 'clausula_particular',
  );
  return (
    <Card>
      <CardHeader title="Condicoes particulares" />
      <CardBody>
        {termos.isError ? (
          <Alert
            tone="danger"
            title="Nao foi possivel carregar as condicoes particulares"
          >
            {termos.error.message}
          </Alert>
        ) : termos.isPending ? (
          <p className="text-sm text-ink-500">Carregando as condicoes particulares.</p>
        ) : clausulas.length === 0 ? (
          <p className="text-sm text-ink-600">Nenhuma condicao particular escolhida.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm text-ink-800">
            {clausulas.map((clausula) => (
              <li key={clausula.id}>
                {clausula.name}
                {clausula.enabled ? '' : ' (fora da minuta)'}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function Item({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-2xs text-ink-500">{rotulo}</dt>
      <dd className="text-ink-800">{children}</dd>
    </div>
  );
}
